/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const API = `${protectedResources.apiTodoList.endpoint}/anticipate/dashboard/home-summary`;
const BRAND = "#52009b";
const SNAPSHOT_KEY = "homeDashboardSnapshot";

// Sticker 1: one consistent hover affordance for all white clickable boxes (cards, cells, etc.).
const HOVER_SHADOW = "0 4px 12px rgba(82,0,155,0.22)";
// Fixed Level-1 header height so all column headers line up regardless of name length / zoom.
const L1_HEADER_H = 58;

// Shared height for the two-line header bands in the Watchlist card so all
// three columns (Total, RAG grid, Optimistic/Pessimistic) line up row-for-row.
const HEADER_BAND_H = "1.75rem";

const RATING_COLOR = { E: "#c62828", H: "#ef6c00", M: "#f9a825", L: "#388e3c", VL: "#1b5e20" };
const RATING_TEXT_DARK = ["M", "VL"];
// V2 priority bands (P1–P5). Band N is coloured the SAME as its matrix rating
// (P1=E red, P2=H orange, P3=M yellow, P4=L green, P5=VL dark green) so it stays
// visually consistent with the rest of the app.
const BAND_COLOR = { 1: "#c62828", 2: "#ef6c00", 3: "#f9a825", 4: "#388e3c", 5: "#1b5e20" };
const BAND_TEXT_DARK = [3]; // P3 (yellow) needs dark text
const BAND_LABEL = { 1: "P1", 2: "P2", 3: "P3", 4: "P4", 5: "P5" };
const ALERT_BG = { red: "#dc2626", amber: "#d97706" };

// ── Small pieces ────────────────────────────────────────────────────────────
function AlertBadge({ status }) {
  if (!status || status === "none")
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  return (
    <Box
      sx={{
        display: "inline-flex",
        px: "8px",
        py: "2px",
        borderRadius: "4px",
        bgcolor: ALERT_BG[status] || "#9ca3af",
        color: "#fff",
        fontSize: "0.68rem",
        fontWeight: 700,
      }}
    >
      {status.toUpperCase()}
    </Box>
  );
}

function RatingBadge({ rating }) {
  if (!rating)
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  return (
    <Box
      sx={{
        display: "inline-flex",
        width: 22,
        height: 22,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "4px",
        bgcolor: RATING_COLOR[rating] || "#90a4ae",
        color: RATING_TEXT_DARK.includes(rating) ? "#212121" : "#fff",
        fontSize: "0.7rem",
        fontWeight: 800,
      }}
    >
      {rating}
    </Box>
  );
}

function TrendChip({ direction }) {
  if (direction === "worsening") {
    return <Box sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#b71c1c" }}>⚠ Worsening</Box>;
  }
  if (direction === "improving") {
    return <Box sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#1b5e20" }}>✓ Improving</Box>;
  }
  return (
    <Typography variant="caption" color="text.secondary">
      ~ Stable
    </Typography>
  );
}

// Highlight wrapper — shows an orange dot + tooltip when value changed since last visit
function Changed({ changed, prev, children }) {
  if (!changed) return children;
  return (
    <Tooltip title={`Was: ${prev}`} placement="top">
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        {children}
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#f97316", flexShrink: 0 }} />
      </Box>
    </Tooltip>
  );
}

// ── KPI cards ───────────────────────────────────────────────────────────────
// Vertical module label strip (left edge of each card)
function ModuleStrip({ label, color }) {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: 20,
        bgcolor: color,
        borderRadius: "6px 0 0 6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        sx={{
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
          fontSize: 9,
          fontWeight: 700,
          color: "#fff",
          letterSpacing: 1,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function WatchlistCard({ kpi, snap, selected, onClick }) {
  const k = kpi || {};
  return (
    <Box
      onClick={onClick}
      sx={{
        // Grows to fill the row's slack; never shrinks below its content (so it can't overflow).
        flex: "1 0 auto",
        minWidth: 0,
        border: "2px solid #52009b",
        borderRadius: 2,
        p: 1.25,
        bgcolor: selected ? "#ede7f6" : "#fff",
        boxShadow: selected ? "0 0 0 2px #52009b inset" : "none",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        "&:hover": { boxShadow: HOVER_SHADOW },
      }}
    >
      <ModuleStrip label="Anticipate" color="#52009b" />

      <Box
        sx={{
          pl: 1.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          gap: 0.75,
          flex: 1,
        }}
      >
        {/* 1. Total + direction arrows, with Optimistic / Pessimistic stacked beneath (keeps the
            box narrow — two columns instead of three — so four boxes fit across). */}
        <Box sx={{ textAlign: "center", flexShrink: 0 }}>
          {/* header band — matches the Approaching/Exceeding header height so the body lines up with the grid */}
          <Box
            sx={{
              fontSize: "0.56rem",
              fontWeight: 600,
              lineHeight: 1.2,
              mb: 0.25,
              height: HEADER_BAND_H,
              overflow: "hidden",
              color: "text.secondary",
            }}
          >
            Total
            <br />
            &nbsp;
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
            <MDTypography variant="h3" fontWeight="bold" sx={{ color: "#52009b", lineHeight: 1 }}>
              W
              <Changed changed={snap && snap.total !== k.total} prev={snap?.total}>
                {k.total ?? 0}
              </Changed>
            </MDTypography>
            <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
              <Box
                component="span"
                sx={{ fontSize: "0.95rem", color: "#1565c0", transform: "rotate(-45deg)" }}
              >
                ➜
              </Box>
              <Box
                component="span"
                sx={{ fontSize: "0.95rem", color: "#b71c1c", transform: "rotate(45deg)" }}
              >
                ➜
              </Box>
            </Box>
          </Box>
        </Box>

        {/* 2 + 3. Approaching / Exceeding headers (two lines) over the RAG grid */}
        <Box sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              gap: "2px",
              mb: 0.25,
              fontSize: "0.56rem",
              fontWeight: 600,
              height: HEADER_BAND_H,
              overflow: "hidden",
            }}
          >
            <Box sx={{ color: "#d97706", textAlign: "center", lineHeight: 1.2, width: 52 }}>
              Approaching
              <br />
              Signposts
            </Box>
            <Box sx={{ color: "#dc2626", textAlign: "center", lineHeight: 1.2, width: 52 }}>
              Exceeding
              <br />
              Signposts
            </Box>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "2px",
              fontSize: "0.62rem",
              fontWeight: 700,
            }}
          >
            <RagCell color="#388e3c" label="Green" value={k.proximity?.green ?? 0} />
            <RagCell color="#f9a825" label="Gold" value={k.proximity?.gold ?? 0} dark />
            <RagCell color="#fb8c00" label="Amber" value={k.proximity?.amber ?? 0} />
            <RagCell color="#dc2626" label="Red" value={k.proximity?.red ?? 0} />
          </Box>
        </Box>

        {/* 4. Optimistic / Pessimistic */}
        <Box sx={{ flexShrink: 0 }}>
          {/* hidden header band so the two lines align with the grid rows */}
          <Box
            sx={{
              fontSize: "0.56rem",
              fontWeight: 600,
              lineHeight: 1.2,
              mb: 0.25,
              height: HEADER_BAND_H,
              overflow: "hidden",
              visibility: "hidden",
            }}
          >
            x
            <br />x
          </Box>
          <Box sx={{ fontSize: "0.62rem", lineHeight: 1.7, whiteSpace: "nowrap" }}>
            <Box sx={{ color: "#1565c0", fontWeight: 600 }}>Best case: {k.optimistic ?? 0}</Box>
            <Box sx={{ color: "#b71c1c", fontWeight: 600 }}>Worst case: {k.pessimistic ?? 0}</Box>
          </Box>
        </Box>
      </Box>

      <MDTypography
        variant="caption"
        sx={{ pl: 1.5, color: "#52009b", fontWeight: 700, textAlign: "center", mt: 0.5 }}
      >
        Watchlist
      </MDTypography>
    </Box>
  );
}

// One RAG cell in the watchlist grid
function RagCell({ color, label, value, dark }) {
  return (
    <Box
      sx={{
        bgcolor: color,
        color: dark ? "#212121" : "#fff",
        borderRadius: "3px",
        px: "5px",
        py: "1px",
        display: "flex",
        justifyContent: "space-between",
        gap: 0.5,
        minWidth: 52,
      }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </Box>
  );
}

function ThemeCard({ kpi, snap, selected, onClick }) {
  const k = kpi || {};
  const matrix = k.ratingMatrix || {};
  // V2 tenants surface priority bands (P1–P5) instead of the legacy E/H matrix ratings.
  const usingV2 = !!(k.usingV2 && k.bandMatrix);
  const bandMatrix = k.bandMatrix || {};
  // Show only the top two priority bands (P1/P2) — the "what to act on now" tier — mirroring the
  // legacy card which showed only the top-two E/H ratings.
  const bands = [1, 2];
  // The card shows E/H ratings (legacy) or the P1/P2 bands (V2); the Total must tally to
  // whatever counts are actually shown.
  const shownTotal = usingV2
    ? bands.reduce(
        (sum, b) => sum + (bandMatrix[b]?.improving ?? 0) + (bandMatrix[b]?.worsening ?? 0),
        0
      )
    : (matrix.E?.improving ?? 0) +
      (matrix.E?.worsening ?? 0) +
      (matrix.H?.improving ?? 0) +
      (matrix.H?.worsening ?? 0);
  return (
    <Box
      onClick={onClick}
      sx={{
        // See WatchlistCard: grows to fill slack, never shrinks below content (no overflow).
        flex: "1 0 auto",
        minWidth: 0,
        border: "2px solid #c2185b",
        borderRadius: 2,
        p: 1.25,
        // Sticker 2: selected Emerging Themes card gets a slight tint, like the Watchlist card.
        bgcolor: selected ? "#fce4ec" : "#fff",
        boxShadow: selected ? "0 0 0 2px #c2185b inset" : "none",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        "&:hover": { boxShadow: HOVER_SHADOW },
      }}
    >
      <ModuleStrip label="Anticipate" color="#c2185b" />
      <Box
        sx={{
          pl: 1.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          gap: 1,
          flex: 1,
        }}
      >
        {/* 1. Total — header band matches the E/H header height so the number lines up (sticker 3) */}
        <Box sx={{ textAlign: "center", flexShrink: 0 }}>
          <Box
            sx={{
              fontSize: "0.56rem",
              fontWeight: 600,
              lineHeight: 1.2,
              mb: 0.25,
              height: HEADER_BAND_H,
              overflow: "hidden",
              color: "text.secondary",
            }}
          >
            Total
            <br />
            &nbsp;
          </Box>
          {/* Sticker 4: total colour matches the Emerging Themes box colour. */}
          <MDTypography variant="h3" fontWeight="bold" sx={{ color: "#c2185b", lineHeight: 1 }}>
            T{shownTotal}
          </MDTypography>
        </Box>

        {/* 2 + 3. E / H × improving / worsening (4 boxes) with row markers:
            ✓ = improving (top row), ✗ = worsening (bottom row). */}
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}>
          {/* Row markers */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ height: 22, mb: 0.25 }} />
            <Box
              sx={{
                height: 20,
                display: "flex",
                alignItems: "center",
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "#1b5e20",
              }}
            >
              ✓
            </Box>
            <Box
              sx={{
                height: 20,
                display: "flex",
                alignItems: "center",
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "#b71c1c",
              }}
            >
              ✗
            </Box>
          </Box>
          {usingV2
            ? // V2: five priority-band columns (P1–P5), slightly narrower to fit.
              bands.map((b) => (
                <Box key={b} sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 22,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "2px",
                      borderRadius: "4px",
                      bgcolor: BAND_COLOR[b],
                      color: BAND_TEXT_DARK.includes(b) ? "#212121" : "#fff",
                      fontSize: "0.6rem",
                      fontWeight: 800,
                      mb: 0.25,
                    }}
                  >
                    {/* band label only — the improving/worsening counts are shown below */}
                    {BAND_LABEL[b]}
                  </Box>
                  <Box
                    sx={{
                      width: 30,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(0,0,0,0.15)",
                      borderBottom: "none",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "#1b5e20",
                      bgcolor: "#fafafa",
                    }}
                  >
                    {bandMatrix[b]?.improving ?? 0}
                  </Box>
                  <Box
                    sx={{
                      width: 30,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(0,0,0,0.15)",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "#b71c1c",
                      bgcolor: "#fafafa",
                    }}
                  >
                    {bandMatrix[b]?.worsening ?? 0}
                  </Box>
                </Box>
              ))
            : ["E", "H"].map((r) => (
                <Box key={r} sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 34,
                      height: 22,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "3px",
                      borderRadius: "4px",
                      bgcolor: RATING_COLOR[r],
                      color: RATING_TEXT_DARK.includes(r) ? "#212121" : "#fff",
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      mb: 0.25,
                    }}
                  >
                    {/* rating letter + total count for that rating (improving + worsening) */}
                    {r} {(matrix[r]?.improving ?? 0) + (matrix[r]?.worsening ?? 0)}
                  </Box>
                  <Box
                    sx={{
                      width: 34,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(0,0,0,0.15)",
                      borderBottom: "none",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "#1b5e20",
                      bgcolor: "#fafafa",
                    }}
                  >
                    {matrix[r]?.improving ?? 0}
                  </Box>
                  <Box
                    sx={{
                      width: 34,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(0,0,0,0.15)",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "#b71c1c",
                      bgcolor: "#fafafa",
                    }}
                  >
                    {matrix[r]?.worsening ?? 0}
                  </Box>
                </Box>
              ))}
        </Box>

        {/* 4. Improving / Worsening badges — aligned with the improving / worsening box rows */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* spacer matching the E/H badge header row so the badges line up with the cells */}
          <Box sx={{ height: 22, mb: 0.25 }} />
          <Box
            sx={{
              height: 20,
              display: "inline-flex",
              alignItems: "center",
              px: "8px",
              borderRadius: "4px",
              fontSize: "0.62rem",
              fontWeight: 700,
              bgcolor: "#e8f5e9",
              color: "#1b5e20",
              border: "1px solid #a5d6a7",
            }}
          >
            ✓ Improving
          </Box>
          <Box
            sx={{
              height: 20,
              display: "inline-flex",
              alignItems: "center",
              px: "8px",
              borderRadius: "4px",
              fontSize: "0.62rem",
              fontWeight: 700,
              bgcolor: "#ffebee",
              color: "#b71c1c",
              border: "1px solid #ef9a9a",
            }}
          >
            ✗ Worsening
          </Box>
        </Box>
      </Box>
      <MDTypography
        variant="caption"
        sx={{ pl: 1.5, color: "#c2185b", fontWeight: 700, textAlign: "center" }}
      >
        Emerging Themes
      </MDTypography>
    </Box>
  );
}

// ── Mini graphics for the taxonomy cells (compact versions of the KPI cards) ──
// Mini watchlist block: Total + 2×2 RAG proximity grid (like the Watchlist card)
function MiniWatchlist({ count, prox, opp, compact }) {
  const p = prox || { green: 0, gold: 0, amber: 0, red: 0 };
  // Sticker: the left total must always tally to the sum of the boxes on the right.
  const wTotal =
    (Number(p.green) || 0) + (Number(p.gold) || 0) + (Number(p.amber) || 0) + (Number(p.red) || 0);
  // Sticker 7/8: boxes are coloured only when they carry a value; otherwise grey.
  const cell = (color, value, dark) => {
    const has = Number(value) > 0;
    return (
      <Box
        sx={{
          bgcolor: has ? color : "#eceff1",
          color: has ? (dark ? "#212121" : "#fff") : "#b0bec5",
          borderRadius: "2px",
          px: "3px",
          minHeight: 13,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.5rem",
          fontWeight: 700,
          lineHeight: 1.35,
        }}
      >
        <span>{value}</span>
      </Box>
    );
  };
  return (
    <Box sx={{ display: "flex", alignItems: "stretch", gap: 0.5 }}>
      <Box
        sx={{
          width: 20,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: BRAND, lineHeight: 1 }}>
          W{wTotal}
        </MDTypography>
      </Box>
      {/* blue ↗ (top, optimistic) / red ↘ (bottom, pessimistic) arrows aligned with the box rows */}
      <Box
        sx={{
          width: 12,
          flexShrink: 0,
          display: "grid",
          gridTemplateRows: "1fr 1fr",
          gap: "2px",
          justifyItems: "center",
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: "0.6rem",
            color: "#1565c0",
            transform: "rotate(-45deg)",
            display: "flex",
            alignItems: "center",
          }}
        >
          ➜
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: "0.6rem",
            color: "#b71c1c",
            transform: "rotate(45deg)",
            display: "flex",
            alignItems: "center",
          }}
        >
          ➜
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: "2px",
          flex: 1,
        }}
      >
        {cell("#388e3c", p.green)}
        {cell("#f9a825", p.gold, true)}
        {cell("#fb8c00", p.amber)}
        {cell("#dc2626", p.red)}
      </Box>
      {!compact && opp && (
        <Box sx={{ fontSize: "0.45rem", lineHeight: 1.4, fontWeight: 700, flexShrink: 0 }}>
          <Box sx={{ color: "#1565c0" }}>B:{opp.optimistic}</Box>
          <Box sx={{ color: "#b71c1c" }}>W:{opp.pessimistic}</Box>
        </Box>
      )}
    </Box>
  );
}

// Mini themes block: theme count + 2×2 grid — columns E / H, rows improving / worsening,
// with a green tick (improving) / red cross (worsening) marking the rows on the left.
// Mirrors the watchlist RAG 2×2 above so the boxes line up.
function MiniThemes({ count, matrix, bandMatrix, bandCounts }) {
  // V2: a compact horizontal strip of the non-zero priority bands (one tiny coloured
  // box per band with its count). L2 cells usually carry just one V2 theme, so this
  // stays tighter than the legacy 2×2 improving/worsening grid.
  if (bandCounts) {
    const bands = [1, 2, 3, 4, 5];
    const bandTotal = bands.reduce((sum, b) => sum + (Number(bandCounts[b]) || 0), 0);
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#c2185b", lineHeight: 1 }}>
            T{bandTotal}
          </MDTypography>
        </Box>
        {bands
          .filter((b) => (Number(bandCounts[b]) || 0) > 0)
          .map((b) => (
            <Box
              key={b}
              sx={{
                bgcolor: BAND_COLOR[b],
                color: BAND_TEXT_DARK.includes(b) ? "#212121" : "#fff",
                borderRadius: "2px",
                px: "3px",
                minHeight: 13,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "2px",
                fontSize: "0.5rem",
                fontWeight: 700,
                lineHeight: 1.35,
                whiteSpace: "nowrap",
              }}
            >
              <span>{BAND_LABEL[b]}</span>
              <span>{Number(bandCounts[b]) || 0}</span>
            </Box>
          ))}
      </Box>
    );
  }
  const m = matrix || {};
  const e = m.E || { improving: 0, worsening: 0 };
  const h = m.H || { improving: 0, worsening: 0 };
  // Sticker: the left total must always tally to the sum of the boxes on the right.
  const tTotal =
    (Number(e.improving) || 0) +
    (Number(e.worsening) || 0) +
    (Number(h.improving) || 0) +
    (Number(h.worsening) || 0);
  // Sticker 7/8: coloured only when a value is present; otherwise grey.
  const cell = (color, value) => {
    const has = Number(value) > 0;
    return (
      <Box
        sx={{
          bgcolor: has ? color : "#eceff1",
          color: has ? "#fff" : "#b0bec5",
          borderRadius: "2px",
          px: "3px",
          minHeight: 13,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.5rem",
          fontWeight: 700,
          lineHeight: 1.35,
        }}
      >
        <span>{value}</span>
      </Box>
    );
  };
  return (
    <Box sx={{ display: "flex", alignItems: "stretch", gap: 0.5 }}>
      <Box
        sx={{
          width: 20,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#c2185b", lineHeight: 1 }}>
          T{tTotal}
        </MDTypography>
      </Box>
      {/* green tick (improving, top row) / red cross (worsening, bottom row) */}
      <Box
        sx={{
          width: 12,
          flexShrink: 0,
          display: "grid",
          gridTemplateRows: "1fr 1fr",
          gap: "2px",
          justifyItems: "center",
        }}
      >
        <Box
          component="span"
          sx={{ fontSize: "0.6rem", color: "#1b5e20", display: "flex", alignItems: "center" }}
        >
          ✓
        </Box>
        <Box
          component="span"
          sx={{ fontSize: "0.6rem", color: "#b71c1c", display: "flex", alignItems: "center" }}
        >
          ✗
        </Box>
      </Box>
      {/* columns E / H, rows improving / worsening */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: "2px",
          flex: 1,
        }}
      >
        {cell(RATING_COLOR.E, e.improving)}
        {cell(RATING_COLOR.H, h.improving)}
        {cell(RATING_COLOR.E, e.worsening)}
        {cell(RATING_COLOR.H, h.worsening)}
      </Box>
    </Box>
  );
}

// ID13 — risk appetite colour scale (risk-averse → opportunity-seeking).
const APPETITE_COLOR = {
  "Risk aversion": "#d32f2f",
  "Balanced risk aversion": "#f57c00",
  "Balanced opportunity": "#fbc02d",
  Opportunity: "#388e3c",
};

// Tiny label above each mini-block in a taxonomy cell (Watchlist / Emerging Themes).
function CellBlockLabel({ text, color }) {
  return (
    <MDTypography
      sx={{
        display: "block",
        fontSize: "0.46rem",
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: 0.3,
        textTransform: "uppercase",
        color,
        mb: "1px",
      }}
    >
      {text}
    </MDTypography>
  );
}

// ── Taxonomy cell ─────────────────────────────────────────────────────────────
// Both the Watchlist + Emerging-Themes mini-blocks always render (each labelled);
// selecting a top card highlights the cells that carry a value for that type.
function TaxonomyCell({ sub, onHover, onClick, cardView, selected }) {
  const showThemes = cardView === "themes";
  const showWatch = cardView === "watchlist";
  const riskCount = sub.riskCount || 0;
  const themeCount = sub.themeCount || 0;
  // Highlight is based on the values actually shown in the boxes (which the W/T totals tally to):
  // watchlist = sum of the RAG proximity boxes; themes = sum of the E/H boxes only.
  const px = sub.proximity || {};
  const watchBoxTotal = (px.green || 0) + (px.gold || 0) + (px.amber || 0) + (px.red || 0);
  const rm = sub.ratingMatrix || {};
  // V2 cells tally the priority-band counts; legacy cells tally the E/H boxes.
  const themeBoxTotal = sub.bandCounts
    ? [1, 2, 3, 4, 5].reduce((sum, b) => sum + (Number(sub.bandCounts[b]) || 0), 0)
    : (rm.E?.improving || 0) +
      (rm.E?.worsening || 0) +
      (rm.H?.improving || 0) +
      (rm.H?.worsening || 0);
  // Sticker 3: selecting a top card no longer hides either block — both always render.
  // Cells that carry a value for the selected card type are highlighted with a frame.
  const highlighted = (showWatch && watchBoxTotal > 0) || (showThemes && themeBoxTotal > 0);
  const alertBorder =
    sub.worstAlert === "red"
      ? "#ef9a9a"
      : sub.worstAlert === "amber"
      ? "#ffe082"
      : "rgba(0,0,0,0.12)";
  // ID13/ID139 — left bar shows the risk appetite in the ADMIN-CONFIGURED colour (from the appetite
  // levels settings), falling back to the legacy scale map. Outer border still conveys alert status.
  const appetiteColor = sub.appetiteColor || APPETITE_COLOR[sub.appetite] || "#cfd8dc";
  return (
    <Box
      onMouseEnter={() => onHover(sub)}
      onClick={onClick}
      title={sub.appetite ? `Appetite: ${sub.appetite}` : undefined}
      sx={{
        border: `1px solid ${alertBorder}`,
        // Sticker 6: appetite colour bar on the left, ~2× thicker, on every L2 cell.
        borderLeft: `10px solid ${appetiteColor}`,
        borderRadius: 1,
        p: 0.75,
        mb: 0.5,
        bgcolor: selected ? "#ede7f6" : highlighted ? "#f3edfb" : "#fff",
        boxShadow: selected
          ? "0 0 0 2px #52009b inset"
          : highlighted
          ? "0 0 0 3px #52009b, 0 2px 8px rgba(82,0,155,0.35)"
          : "none",
        cursor: "pointer",
        transition: "box-shadow 0.15s",
        // Sticker 1: consistent hover — white boxes get a shadow.
        "&:hover": {
          boxShadow: selected ? "0 0 0 2px #52009b inset" : HOVER_SHADOW,
        },
      }}
    >
      <MDTypography
        variant="caption"
        sx={{
          fontSize: "0.62rem",
          color: "#555",
          display: "block",
          lineHeight: 1.2,
          mb: 0.25,
          minHeight: 24,
        }}
      >
        {sub.name || "—"}
      </MDTypography>
      {/* Sticker 1 (this view): label each mini-block so the two are distinguishable now both show. */}
      <CellBlockLabel text="Watchlist" color={BRAND} />
      <MiniWatchlist count={riskCount} prox={sub.proximity} opp={sub.oppThreat} compact />
      <Box sx={{ mt: 0.5 }}>
        <CellBlockLabel text="Emerging Themes" color="#c2185b" />
        <MiniThemes
          count={themeCount}
          matrix={sub.ratingMatrix}
          bandMatrix={sub.bandMatrix}
          bandCounts={sub.bandCounts}
        />
      </Box>
    </Box>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AnticipateDashboard() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);

  // Resolve the L1 category owner email → display name (mirrors the Taxonomy page).
  const [ownerUsers, setOwnerUsers] = useState([]);
  const ownerUsersFetched = useRef(false);
  useEffect(() => {
    if (ownerUsersFetched.current) return;
    ownerUsersFetched.current = true;
    executeRef
      .current("GET", `${protectedResources.apiTodoList.endpoint}/users/list-for-owners`)
      .then((res) => {
        if (Array.isArray(res?.data)) setOwnerUsers(res.data);
      })
      .catch(() => {});
  }, []);
  const ownerName = (email) => {
    if (!email) return "";
    const u = ownerUsers.find((x) => (x.email || "").toLowerCase() === String(email).toLowerCase());
    if (!u) return email;
    const full = [u.firstName || "", u.lastName || ""].filter(Boolean).join(" ").trim();
    return full || u.name || u.email || email;
  };
  const [hoverCell, setHoverCell] = useState(null);
  const [showChange] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  // Interactions
  const [selectedCategory, setSelectedCategory] = useState(""); // clicked taxonomy column (L1) → filters tables
  const [selectedSub, setSelectedSub] = useState(null); // clicked taxonomy box (L2) → { category, name }
  const [detailItem, setDetailItem] = useState(null); // { type: 'risk'|'theme', data } → summary box
  const [cardView, setCardView] = useState("both"); // "both" | "watchlist" | "themes" — KPI card toggle
  const [changesView, setChangesView] = useState(false); // right panel shows "changes since last visit"
  const [hideEmpty, setHideEmpty] = useState(true); // hide taxonomy cells with no risks and no themes

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await executeRef.current("GET", API);
      const d = res?.data || null;
      setData(d);
      // Change detection: the stored snapshot only advances when the data DAY changes, so "changes
      // since last visit" persists across same-day reloads instead of erasing itself on the first
      // load (which made the feature appear broken for an active user).
      if (d) {
        const cells = {};
        (d.taxonomyGrid || []).forEach((col) => {
          (col.subCategories || []).forEach((s) => {
            cells[`${col.category}||${s.name}`] = {
              riskCount: s.riskCount || 0,
              themeCount: s.themeCount || 0,
            };
          });
        });
        const currSnap = { watchlistKpi: d.watchlistKpi, themeKpi: d.themeKpi, cells };
        const version = d.todayUtc || new Date().toISOString().slice(0, 10);
        let stored = null;
        try {
          stored = JSON.parse(localStorage.getItem(SNAPSHOT_KEY) || "null");
        } catch {
          stored = null;
        }
        // Migrate an old flat snapshot (no `.v`) → treat it as the previous day's data.
        if (stored && stored.v === undefined) stored = { v: "", curr: stored, prev: null };
        const persist = (obj) => {
          try {
            localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(obj));
          } catch {
            /* storage unavailable — ignore */
          }
        };
        if (!stored) {
          setSnapshot(null); // first visit — nothing to compare
          persist({ v: version, curr: currSnap, prev: null });
        } else if (stored.v === version) {
          setSnapshot(stored.prev || null); // same day → keep baseline, don't overwrite
        } else {
          setSnapshot(stored.curr || null); // new day → diff vs last day, then advance
          persist({ v: version, curr: currSnap, prev: stored.curr || null });
        }
      } else {
        setSnapshot(null);
      }
    } catch {
      setError("Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Hide categories/sub-categories with no risks AND no themes (default on) to simplify the grid.
  const rawGrid = data?.taxonomyGrid || [];
  const grid = hideEmpty
    ? rawGrid
        .map((col) => ({
          ...col,
          subCategories: (col.subCategories || []).filter(
            (s) => (s.riskCount || 0) > 0 || (s.themeCount || 0) > 0
          ),
        }))
        .filter((col) => (col.subCategories || []).length > 0)
    : rawGrid;
  const hiddenCount =
    rawGrid.reduce((n, c) => n + (c.subCategories || []).length, 0) -
    grid.reduce((n, c) => n + (c.subCategories || []).length, 0);
  const allRisks = data?.allRisks || [];
  const allThemes = data?.allThemes || [];

  // Sticker 4: open the whole taxonomy by default (fit to width), then let the user zoom.
  const gridScrollRef = useRef(null);
  const didFitRef = useRef(false);
  const computeFitZoom = useCallback(() => {
    const el = gridScrollRef.current;
    if (!el || !grid.length) return null;
    const COL_W = 168; // ~150 min + padding + border + gap per column
    const naturalW = grid.length * COL_W;
    const containerW = el.clientWidth - 8;
    if (containerW <= 0 || naturalW <= 0) return null;
    return Math.max(0.6, Math.min(1, +(containerW / naturalW).toFixed(2)));
  }, [grid.length]);
  useEffect(() => {
    if (didFitRef.current || !grid.length) return;
    const f = computeFitZoom();
    if (f) {
      setZoom(f);
      didFitRef.current = true;
    }
  }, [grid.length, computeFitZoom]);
  const fitToWidth = () => {
    const f = computeFitZoom();
    if (f) setZoom(f);
  };
  // The right panel is driven by a single "active cell" so hover and click behave
  // identically: hovering a cell sets it transiently; clicking a cell pins it.
  const selectedCell = selectedSub
    ? (() => {
        const col = grid.find((c) => c.category === selectedSub.category);
        const s = (col?.subCategories || []).find((x) => x.name === selectedSub.name);
        return s ? { ...s, parentCategory: selectedSub.category } : null;
      })()
    : null;
  const activeCell = hoverCell || selectedCell;
  const effSub = activeCell ? { category: activeCell.parentCategory, name: activeCell.name } : null;
  // Selected L1 category summary (Title + aggregated counts) when no specific L2 cell is active.
  const selectedColumn =
    selectedCategory && !activeCell ? grid.find((c) => c.category === selectedCategory) : null;
  const selectedColumnSummary = selectedColumn
    ? {
        name: selectedColumn.category,
        riskCount: (selectedColumn.subCategories || []).reduce((s, x) => s + (x.riskCount || 0), 0),
        themeCount: (selectedColumn.subCategories || []).reduce(
          (s, x) => s + (x.themeCount || 0),
          0
        ),
        worstAlert: "none",
      }
    : null;
  // Filter precedence: active L2 cell → selected L1 column → top-10 lists.
  const topRisks = effSub
    ? allRisks.filter((r) => r.category === effSub.category && r.subCategory === effSub.name)
    : selectedCategory
    ? allRisks.filter((r) => r.category === selectedCategory)
    : data?.topRisks || [];
  const topThemes = effSub
    ? allThemes.filter((t) => t.category === effSub.category && t.subCategory === effSub.name)
    : selectedCategory
    ? allThemes.filter((t) => t.category === selectedCategory)
    : data?.topThemes || [];
  const wSnap = showChange ? snapshot?.watchlistKpi : null;
  const tSnap = showChange ? snapshot?.themeKpi : null;

  // "Changes since last visit" — per-cell risk/theme count deltas vs the saved snapshot.
  const changesList = [];
  if (snapshot?.cells) {
    grid.forEach((col) => {
      (col.subCategories || []).forEach((s) => {
        const prev = snapshot.cells[`${col.category}||${s.name}`];
        const rc = s.riskCount || 0;
        const tc = s.themeCount || 0;
        if (!prev) {
          if (rc || tc)
            changesList.push({
              category: col.category,
              name: s.name,
              riskDelta: rc,
              themeDelta: tc,
              isNew: true,
            });
          return;
        }
        const rd = rc - (prev.riskCount || 0);
        const td = tc - (prev.themeCount || 0);
        if (rd !== 0 || td !== 0)
          changesList.push({ category: col.category, name: s.name, riskDelta: rd, themeDelta: td });
      });
    });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={2}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: BRAND }} />
          </Box>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && data && (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                flexWrap: { xs: "wrap", lg: "nowrap" },
              }}
            >
              {/* ── Left column: KPI strip + taxonomy ── */}
              <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                {/* KPI strip — responsive grid so the four boxes never wrap to an awkward 3+1:
                    1 column on narrow, 2×2 on laptops, 4-across on wide screens where they fit. */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 2,
                    flexWrap: "wrap",
                    alignItems: "stretch",
                  }}
                >
                  <WatchlistCard
                    kpi={data.watchlistKpi}
                    snap={wSnap}
                    selected={cardView === "watchlist"}
                    onClick={() => {
                      setCardView((v) => (v === "watchlist" ? "both" : "watchlist"));
                      setSelectedCategory("");
                      setSelectedSub(null);
                      setDetailItem(null);
                    }}
                  />
                  <ThemeCard
                    kpi={data.themeKpi}
                    snap={tSnap}
                    selected={cardView === "themes"}
                    onClick={() => {
                      setCardView((v) => (v === "themes" ? "both" : "themes"));
                      setSelectedCategory("");
                      setSelectedSub(null);
                      setDetailItem(null);
                    }}
                  />
                  {/* Show Change toggle — hidden for now
                  <Box
                    onClick={() => setShowChange((s) => !s)}
                    sx={{
                      flex: "0 0 auto",
                      display: "flex",
                      alignItems: "center",
                      px: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: showChange ? "#f97316" : "rgba(0,0,0,0.15)",
                      bgcolor: showChange ? "#fff7ed" : "transparent",
                      cursor: "pointer",
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      color: showChange ? "#c2410c" : "#666",
                    }}
                  >
                    {showChange ? "● Showing changes" : "○ Show changes since last visit"}
                  </Box>
                  */}
                </Box>

                {/* ── Taxonomy grid ── */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <MDTypography variant="h6" fontWeight="medium">
                      Risk Taxonomy
                      {cardView === "watchlist" && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: BRAND }}>
                          · showing watchlist risk counts
                        </Typography>
                      )}
                      {cardView === "themes" && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 1, color: "#c2185b" }}
                        >
                          · showing emerging theme counts
                        </Typography>
                      )}
                    </MDTypography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* Sticker 7: clears any filter/selection and returns the grid to 100%. */}
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={hideEmpty}
                            onChange={(e) => setHideEmpty(e.target.checked)}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: BRAND }}>
                            Hide empty{hideEmpty && hiddenCount > 0 ? ` (${hiddenCount})` : ""}
                          </Typography>
                        }
                        sx={{ mr: 0.5 }}
                      />
                      <Box
                        onClick={() => {
                          setSelectedCategory("");
                          setSelectedSub(null);
                          setCardView("both");
                          setDetailItem(null);
                          fitToWidth();
                        }}
                        title="Clear selection and fit the whole taxonomy to the screen"
                        sx={{
                          px: 1.25,
                          py: 0.4,
                          cursor: "pointer",
                          color: BRAND,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          border: "1px solid rgba(82,0,155,0.3)",
                          borderRadius: "6px",
                          "&:hover": { bgcolor: "rgba(82,0,155,0.06)" },
                        }}
                      >
                        Unselect
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          border: "1px solid rgba(82,0,155,0.3)",
                          borderRadius: "6px",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.2).toFixed(1)))}
                          sx={{
                            px: 1.25,
                            py: 0.25,
                            cursor: "pointer",
                            color: BRAND,
                            fontWeight: 700,
                            borderRight: "1px solid rgba(82,0,155,0.2)",
                          }}
                        >
                          −
                        </Box>
                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.25,
                            fontSize: "0.72rem",
                            color: BRAND,
                            minWidth: 42,
                            textAlign: "center",
                          }}
                        >
                          {Math.round(zoom * 100)}%
                        </Box>
                        <Box
                          onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.2).toFixed(1)))}
                          sx={{
                            px: 1.25,
                            py: 0.25,
                            cursor: "pointer",
                            color: BRAND,
                            fontWeight: 700,
                            borderLeft: "1px solid rgba(82,0,155,0.2)",
                          }}
                        >
                          +
                        </Box>
                        <Box
                          onClick={fitToWidth}
                          title="Fit the whole taxonomy to the screen"
                          sx={{
                            px: 1.25,
                            py: 0.25,
                            cursor: "pointer",
                            color: BRAND,
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            borderLeft: "1px solid rgba(82,0,155,0.2)",
                          }}
                        >
                          Fit
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {grid.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No taxonomy categories configured.
                    </Typography>
                  ) : (
                    <Box
                      ref={gridScrollRef}
                      sx={{
                        overflowX: "scroll",
                        pb: 1,
                        // Always show a visible scrollbar so users know the grid scrolls
                        // horizontally (some browsers hide overlay scrollbars by default).
                        scrollbarColor: "rgba(82,0,155,0.5) rgba(0,0,0,0.06)",
                        "&::-webkit-scrollbar": { height: 12 },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "rgba(0,0,0,0.06)",
                          borderRadius: 6,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "rgba(82,0,155,0.5)",
                          borderRadius: 6,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          // CSS zoom scales the layout box (unlike transform: scale), so the
                          // container shrinks to fit and the horizontal scrollbar stays right
                          // under the grid instead of far down the page when zoomed out.
                          zoom,
                        }}
                      >
                        {grid.map((col) => {
                          const isSelected = selectedCategory === col.category;
                          const dimmed = selectedCategory && !isSelected;
                          return (
                            <Box
                              key={col.category}
                              sx={{
                                flex: "1 0 150px",
                                minWidth: 150,
                                opacity: dimmed ? 0.4 : 1,
                                transition: "opacity 0.15s",
                                // Sticker 6: frame each column so sections stay distinct and aren't
                                // cut off when zoomed.
                                border: "1px solid rgba(82,0,155,0.15)",
                                borderRadius: 1,
                                p: 0.5,
                                bgcolor: "#faf9fc",
                              }}
                            >
                              {/* Column header — click to filter right panel to the whole category */}
                              <Box
                                onClick={() => {
                                  const wholeColSelected = isSelected && !selectedSub;
                                  setSelectedSub(null);
                                  setSelectedCategory(wholeColSelected ? "" : col.category);
                                }}
                                sx={{
                                  bgcolor: isSelected ? "#52009b" : "#ede7f6",
                                  border: "1px solid #ce93d8",
                                  borderRadius: 1,
                                  px: 1,
                                  mb: 0.5,
                                  // Sticker 5: fixed height so all L1 headers line up at any zoom.
                                  height: L1_HEADER_H,
                                  overflow: "hidden",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: isSelected ? "#3d0075" : "#e1d5f0" },
                                }}
                              >
                                <MDTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    lineHeight: 1.2,
                                    color: isSelected ? "#fff" : BRAND,
                                  }}
                                >
                                  {col.category}
                                </MDTypography>
                                {col.executiveAuthority && (
                                  <MDTypography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: isSelected ? "rgba(255,255,255,0.8)" : "#999",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {ownerName(col.executiveAuthority)}
                                  </MDTypography>
                                )}
                              </Box>
                              {/* L2 cells — click to filter the right panel to that one box */}
                              {(col.subCategories || []).map((sub, si) => {
                                const subSelected =
                                  selectedSub &&
                                  selectedSub.category === col.category &&
                                  selectedSub.name === sub.name;
                                return (
                                  <TaxonomyCell
                                    key={si}
                                    sub={sub}
                                    cardView={cardView}
                                    selected={subSelected}
                                    // Hover disabled for now — the right panel updates on click only.
                                    onHover={() => {}}
                                    onClick={() => {
                                      // Select only this sub-category box (don't highlight the
                                      // whole column); column-header clicks still select the column.
                                      setSelectedSub(
                                        subSelected
                                          ? null
                                          : { category: col.category, name: sub.name }
                                      );
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* ── Right panel ── */}
              <Box sx={{ flex: "0 0 266px", maxWidth: { xs: "100%", lg: 266 }, width: "100%" }}>
                {/* Changes-since-last-visit card — click to fill the panel with the change list */}
                <Box
                  onClick={() => setChangesView((v) => !v)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                    mb: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: changesView ? "#f97316" : "rgba(82,0,155,0.25)",
                    bgcolor: changesView ? "#fff7ed" : "#fff",
                    cursor: "pointer",
                    "&:hover": { boxShadow: HOVER_SHADOW },
                  }}
                >
                  <MDTypography
                    variant="caption"
                    sx={{ fontWeight: 700, color: changesView ? "#c2410c" : BRAND }}
                  >
                    {changesView ? "● Showing changes" : "⟳ Changes since last visit"}
                  </MDTypography>
                  {snapshot?.cells && (
                    <Box
                      component="span"
                      sx={{
                        px: "7px",
                        py: "1px",
                        borderRadius: "10px",
                        fontSize: "0.66rem",
                        fontWeight: 700,
                        bgcolor: changesList.length ? "#f97316" : "rgba(0,0,0,0.1)",
                        color: changesList.length ? "#fff" : "#666",
                      }}
                    >
                      {changesList.length}
                    </Box>
                  )}
                </Box>

                {/* Changes view fills the whole box */}
                {changesView ? (
                  <Box
                    sx={{ border: "1px solid rgba(82,0,155,0.2)", borderRadius: 2, p: 1.5, mb: 2 }}
                  >
                    <MDTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Changes since your last visit
                    </MDTypography>
                    {!snapshot?.cells ? (
                      <Typography variant="caption" color="text.secondary">
                        First visit — changes will show next time.
                      </Typography>
                    ) : changesList.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No changes since your last visit.
                      </Typography>
                    ) : (
                      changesList.map((c) => (
                        <Box
                          key={`${c.category}||${c.name}`}
                          onClick={() => {
                            setSelectedSub({ category: c.category, name: c.name });
                            setSelectedCategory(c.category);
                            setChangesView(false);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            py: 0.5,
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            cursor: "pointer",
                            "&:hover": { bgcolor: "rgba(82,0,155,0.04)" },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              sx={{ display: "block", lineHeight: 1.2 }}
                            >
                              {c.name}
                              {c.isNew ? " (new)" : ""}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.6rem" }}
                            >
                              {c.category}
                            </MDTypography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                            {c.riskDelta !== 0 && (
                              <Box
                                component="span"
                                sx={{
                                  px: "5px",
                                  borderRadius: "4px",
                                  fontSize: "0.6rem",
                                  fontWeight: 700,
                                  bgcolor: c.riskDelta > 0 ? "#ede7f6" : "#eee",
                                  color: BRAND,
                                }}
                              >
                                {c.riskDelta > 0 ? `▲${c.riskDelta}` : `▼${Math.abs(c.riskDelta)}`}{" "}
                                risk
                              </Box>
                            )}
                            {c.themeDelta !== 0 && (
                              <Box
                                component="span"
                                sx={{
                                  px: "5px",
                                  borderRadius: "4px",
                                  fontSize: "0.6rem",
                                  fontWeight: 700,
                                  bgcolor: "#fce4ec",
                                  color: "#c2185b",
                                }}
                              >
                                {c.themeDelta > 0
                                  ? `▲${c.themeDelta}`
                                  : `▼${Math.abs(c.themeDelta)}`}{" "}
                                theme
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ))
                    )}
                  </Box>
                ) : (
                  (activeCell || selectedColumnSummary) &&
                  (() => {
                    // Sticker: when a Category (L1) or sub-category (L2) is selected, show just the
                    // Title and the total counts — no separate "Filtered:" line.
                    const detail = activeCell || selectedColumnSummary;
                    return (
                      <Box
                        sx={{
                          border: "1px solid rgba(82,0,155,0.2)",
                          borderRadius: 2,
                          p: 2,
                          mb: 2,
                        }}
                      >
                        <MDTypography variant="subtitle2" fontWeight="bold">
                          {detail.name}
                        </MDTypography>
                        <Box sx={{ display: "flex", gap: 3, my: 1 }}>
                          <Box>
                            <MDTypography variant="h4" fontWeight="bold" sx={{ color: BRAND }}>
                              {detail.riskCount || 0}
                            </MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              watchlist risk{detail.riskCount !== 1 ? "s" : ""}
                            </MDTypography>
                          </Box>
                          <Box>
                            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#c2185b" }}>
                              {detail.themeCount || 0}
                            </MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              emerging theme{detail.themeCount !== 1 ? "s" : ""}
                            </MDTypography>
                          </Box>
                        </Box>
                        {detail.worstAlert && detail.worstAlert !== "none" && (
                          <Box sx={{ mt: 0.5 }}>
                            <AlertBadge status={detail.worstAlert} />
                          </Box>
                        )}
                      </Box>
                    );
                  })()
                )}

                {/* Watchlist Risks (top 10, or filtered by category) */}
                {!changesView && cardView !== "themes" && (
                  <Box
                    sx={{
                      border: "1px solid rgba(82,0,155,0.15)",
                      borderRadius: 2,
                      overflow: "hidden",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ px: 1.5, py: 1, bgcolor: "rgba(82,0,155,0.06)" }}>
                      <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: BRAND }}>
                        {cardView === "watchlist" || selectedCategory
                          ? "Watchlist Risks"
                          : "Top 10 Watchlist Risks"}
                      </MDTypography>
                    </Box>
                    <Table
                      size="small"
                      sx={{
                        tableLayout: "fixed",
                        width: "100%",
                        "& .MuiTableCell-root": { px: "6px", py: "5px" },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: "0.7rem", color: BRAND, width: "54%" }}
                          >
                            Risk
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: "0.7rem", color: BRAND, width: "24%" }}
                          >
                            Time
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: "0.7rem", color: BRAND, width: "22%" }}
                          >
                            Alert
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topRisks.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="caption" color="text.secondary">
                                No active risks.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          topRisks.map((r) => (
                            <TableRow
                              key={r.id}
                              hover
                              sx={{
                                cursor: "pointer",
                                bgcolor:
                                  detailItem?.data?.id === r.id ? "rgba(82,0,155,0.06)" : undefined,
                              }}
                              onClick={() => setDetailItem({ type: "risk", data: r })}
                            >
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    minWidth: 0,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      bgcolor:
                                        r.threatOpp === "opportunity" ? "#3b82f6" : "#ef4444",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.78rem",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {r.title}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {r.timeFrame || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <AlertBadge status={r.alertStatus} />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Emerging Themes (top 10, or filtered by category) */}
                {!changesView && cardView !== "watchlist" && (
                  <Box
                    sx={{
                      border: "1px solid rgba(82,0,155,0.15)",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ px: 1.5, py: 1, bgcolor: "rgba(82,0,155,0.06)" }}>
                      <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: BRAND }}>
                        {cardView === "themes" || selectedCategory
                          ? "Emerging Themes"
                          : "Top 10 Emerging Themes"}
                      </MDTypography>
                    </Box>
                    <Table
                      size="small"
                      sx={{
                        tableLayout: "fixed",
                        width: "100%",
                        "& .MuiTableCell-root": { px: "6px", py: "5px" },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: "0.7rem", color: BRAND, width: "46%" }}
                          >
                            Theme
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: "0.7rem", color: BRAND, width: "36%" }}
                          >
                            Category
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: "0.7rem", color: BRAND, width: "18%" }}
                          >
                            Rating
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topThemes.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="caption" color="text.secondary">
                                No themes yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          topThemes.map((t) => (
                            <TableRow
                              key={t.themeTag}
                              hover
                              sx={{
                                cursor: "pointer",
                                bgcolor:
                                  detailItem?.data?.themeTag === t.themeTag
                                    ? "rgba(82,0,155,0.06)"
                                    : undefined,
                              }}
                              onClick={() => setDetailItem({ type: "theme", data: t })}
                            >
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.78rem",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {t.displayName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {t.consequenceType || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <RatingBadge rating={t.matrixRating} />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Detail summary box (appears when a risk/theme row is clicked) */}
                {!changesView && detailItem && (
                  <Box
                    sx={{
                      border: "2px solid #52009b",
                      borderRadius: 2,
                      p: 2,
                      mt: 2,
                      position: "relative",
                    }}
                  >
                    <Box
                      onClick={() => setDetailItem(null)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 10,
                        cursor: "pointer",
                        color: "#999",
                        fontSize: "0.9rem",
                      }}
                    >
                      ✕
                    </Box>
                    {detailItem.type === "risk" ? (
                      <>
                        <MDTypography variant="h6" fontWeight="bold" sx={{ color: BRAND, pr: 3 }}>
                          {detailItem.data.title}
                        </MDTypography>
                        {detailItem.data.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.78rem", my: 1, lineHeight: 1.5 }}
                          >
                            {detailItem.data.description.length > 280
                              ? `${detailItem.data.description.slice(0, 280)}…`
                              : detailItem.data.description}
                          </Typography>
                        )}
                        {[
                          ["Category", detailItem.data.category],
                          ["Sub-category", detailItem.data.subCategory],
                          ["Time frame", detailItem.data.timeFrame],
                          ["Consequence", detailItem.data.consequence],
                          ["Velocity", detailItem.data.velocity],
                          ["Response", detailItem.data.response],
                          ["Opp / Threat", detailItem.data.oppThreat],
                        ].map(([label, val]) => (
                          <Box
                            key={label}
                            sx={{
                              display: "flex",
                              py: 0.4,
                              borderBottom: "1px solid rgba(0,0,0,0.05)",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ width: 110, color: "#999", flexShrink: 0 }}
                            >
                              {label}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {val || "—"}
                            </Typography>
                          </Box>
                        ))}
                        <Box sx={{ display: "flex", py: 0.4, alignItems: "center" }}>
                          <Typography
                            variant="caption"
                            sx={{ width: 110, color: "#999", flexShrink: 0 }}
                          >
                            Alert
                          </Typography>
                          <AlertBadge status={detailItem.data.alertStatus} />
                        </Box>
                        <MDButton
                          size="small"
                          variant="outlined"
                          onClick={() => navigate("/anticipate/EmergingRisks")}
                          sx={{
                            mt: 1.5,
                            color: BRAND,
                            borderColor: BRAND,
                            textTransform: "none",
                            fontSize: "0.72rem",
                          }}
                        >
                          Open full record
                        </MDButton>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 3 }}>
                          <RatingBadge rating={detailItem.data.matrixRating} />
                          <MDTypography variant="h6" fontWeight="bold" sx={{ color: BRAND }}>
                            {detailItem.data.displayName}
                          </MDTypography>
                        </Box>
                        {detailItem.data.narrative && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.78rem", my: 1, lineHeight: 1.5 }}
                          >
                            {detailItem.data.narrative}
                          </Typography>
                        )}
                        {[
                          ["Consequence type", detailItem.data.consequenceType],
                          ["Consequence level", detailItem.data.consequenceLevelLabel],
                          ["Plausibility", detailItem.data.plausibilityRating],
                        ].map(([label, val]) => (
                          <Box
                            key={label}
                            sx={{
                              display: "flex",
                              py: 0.4,
                              borderBottom: "1px solid rgba(0,0,0,0.05)",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ width: 130, color: "#999", flexShrink: 0 }}
                            >
                              {label}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {val || "—"}
                            </Typography>
                          </Box>
                        ))}
                        <Box sx={{ display: "flex", py: 0.4, alignItems: "center" }}>
                          <Typography
                            variant="caption"
                            sx={{ width: 130, color: "#999", flexShrink: 0 }}
                          >
                            Trend
                          </Typography>
                          <TrendChip direction={detailItem.data.direction} />
                        </Box>
                        <MDButton
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              `/anticipate/HorizonScan?theme=${encodeURIComponent(
                                detailItem.data.themeTag
                              )}`
                            )
                          }
                          sx={{
                            mt: 1.5,
                            color: BRAND,
                            borderColor: BRAND,
                            textTransform: "none",
                            fontSize: "0.72rem",
                          }}
                        >
                          See Horizon Scan stream
                        </MDButton>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <MDButton
                size="small"
                variant="text"
                onClick={load}
                sx={{ color: BRAND, textTransform: "none" }}
              >
                Refresh
              </MDButton>
            </Box>
          </>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
